/**
 * Human Audit tab — IN-PROGRESS editing workspace (BI-1246).
 *
 * Shown while a human-audit session is active (the case's AUDIT_TYPE_HUMAN entity
 * is IN_PROGRESS). Three panes:
 *   - Left:   every call transcript (AI + human) in an accordion so the auditor can
 *             traverse the conversations, with a single search box that highlights a
 *             word across ALL transcripts at once. Each transcript loads lazily — its
 *             assets + transcript fetch only when the item is open (or when a global
 *             search expands them all).
 *   - Middle: the AI auditor's handoff actions (collapsible sidebar), each escalatable to
 *             the human caller with a note or resolvable in place, above the
 *             "Escalate to human caller" list they land in. Shown when the audit carries
 *             handoff actions OR the auditor has escalated an output field.
 *   - Right:  the editable disposition + case summary, then the full editable OUTPUT
 *             board (`FieldGroupAccordion`, section="output") with an All/Missed/Required
 *             filter, in its own collapsible sidebar. While that board is in EDIT mode,
 *             EVERY field on it — at any nesting depth — carries its own Escalate icon
 *             that opens a note box under the field and stages into the same list (see
 *             `FieldEscalationProvider`), so the auditor can hand off a field the AI
 *             auditor never flagged.
 *
 * The working set is the CASE's output_data. Edits persist via a SILENT, debounced
 * `useUpdateCaseOutput` POST — the same endpoint the backend uses to record the
 * per-field human-audit corrections (OriginalValue → CorrectedValue). Disposition
 * is recomputed client-side via `useCommittedDisposition`; the persisted case
 * disposition is reconciled on unmount by firing ACTION_CALCULATE_DISPOSITION.
 *
 * The COMPLETED view (the corrections diff) is rendered separately in
 * DefaultCaseDetailsPage — this component is only the live editing experience.
 */
import { AssetType } from '@compass/client/types/asset';
import { type CallInfo, CallType } from '@compass/client/types/call';
import { type CaseRecord } from '@compass/client/types/case_record';
import { Transcript } from '@components/Transcript/Transcript';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { FieldEscalationProvider } from '@components/ui/field-escalation';
import { FieldGroupAccordion } from '@components/ui/field-group-accordion';
import { Input } from '@components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@components/ui/tabs';
import { Textarea } from '@components/ui/textarea';
import { SimpleTooltip } from '@components/ui/tooltip';
import { ChevronsDownUp, Edit3, Loader, RefreshCw, Save, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CollapsibleSidebar, SidebarCollapseButton } from '@/components/CollapsibleSidebar';
import { FormattedDateTime } from '@/components/FormattedDateTime';
import { OutputBoardEditor } from '@/components/OutputBoardEditor';
import { useCallAssets } from '@/hooks/useCallAssets';
import { useCaseAudit } from '@/hooks/useCaseAudit';
import { useUpdateCaseInput } from '@/hooks/useCases';
import { useCommittedDisposition } from '@/hooks/useCommittedDisposition';
import { useEngineAgentId } from '@/hooks/useEngineAgentId';
import { useOutputEditor } from '@/hooks/useOutputEditor';
import { useIsCaseProgramAdmin, useIsCurrentUserAssigned } from '@/hooks/usePermissions';
import { useSchema } from '@/hooks/useSchema';
import { useCalculateDisposition, useRegenerateSummary } from '@/hooks/useSummary';
import { useTranscript } from '@/hooks/useTranscript';
import { useStore } from '@/store/index';
import {
  createAllAccordionIndices,
  expandAccordionAncestorsAndScrollToField,
  toggleAllAccordionItems,
} from '@/utils/accordion';
import { canWorkHumanAudit } from '@/utils/caseActions';
import { caseSchemaVersion } from '@/utils/caseSchemaVersion';
import { coerceFormDataToSchema } from '@/utils/formSchema';
import {
  handoffFieldLabel,
  normalizeHandoffFieldPath,
  visibleHandoffActions,
} from '@/utils/handoffField';
import { getNestedValue } from '@/utils/nestedAccordion';
import {
  collectPhoneFieldErrors,
  getPhoneFieldError,
  normalizePhoneFieldsForSave,
} from '@/utils/phone';
import { findTextMatches, parseTimestampToSeconds } from '@/utils/transcript';
import { isSummaryEditableWorkflow } from '@/utils/workflow';

import { HandoffActionRow } from './HandoffActionRow';

interface HumanAuditTabProps {
  caseId: string;
  caseDetails: CaseRecord;
  /** Every call on the case (AI + human) — rendered as traversable transcripts. */
  calls: CallInfo[];
}

/**
 * One call's transcript. Rendered inside an AccordionContent so it mounts (and
 * fetches its assets) only when the item is expanded — avoids N transcript fetches
 * up front. `searchQuery` highlights the global search term within this transcript.
 */
const CallTranscriptPane: React.FC<{
  caseId: string;
  call: CallInfo;
  searchQuery: string;
  /** Evidence span (seconds) to highlight when an output field's "Review" is clicked. */
  evidenceHighlightRange?: { start: number; end: number } | null;
}> = ({ caseId, call, searchQuery, evidenceHighlightRange }) => {
  const { data: assets = [], isLoading } = useCallAssets(
    caseId,
    call.id ?? null,
    AssetType.ASSET_TYPE_CALL_TRANSCRIPT,
  );
  return (
    // Fixed height (not max-h + overflow) so the Transcript's OWN internal scroll
    // container handles scrolling and its search/nav bar (up/down arrows) stays
    // pinned at the top instead of scrolling away with the messages.
    <div className="h-96">
      <Transcript
        caseRecordId={caseId}
        callId={call.id!}
        callSid={call.sid}
        assets={assets}
        isLoadingAssets={isLoading}
        callStatus={call.status}
        externalSearchQuery={searchQuery}
        evidenceHighlightRange={evidenceHighlightRange ?? null}
      />
    </div>
  );
};

/**
 * Headless per-call match counter. Fetches the call's transcript (the same cached
 * query the visible Transcript uses, so no double fetch) and reports how many times
 * `query` appears, so the accordion header can show a count pill before the auditor
 * opens it. Renders nothing. Only mounted while a search is active.
 */
const TranscriptMatchCounter: React.FC<{
  caseId: string;
  call: CallInfo;
  query: string;
  onCount: (callId: string, count: number) => void;
}> = ({ caseId, call, query, onCount }) => {
  const { data: assets = [] } = useCallAssets(
    caseId,
    call.id ?? null,
    AssetType.ASSET_TYPE_CALL_TRANSCRIPT,
  );
  const transcriptAsset = assets.length > 0 ? assets[0].id : undefined;
  const { transcript } = useTranscript(caseId, transcriptAsset, {
    callStatus: call.status,
    callSid: call.sid,
  });
  const count = useMemo(() => {
    const q = query.trim();
    if (!q) return 0;
    return transcript.reduce((n, seg) => n + findTextMatches(seg.text, q).length, 0);
  }, [transcript, query]);
  useEffect(() => {
    if (call.id) onCount(call.id, count);
  }, [call.id, count, onCount]);
  return null;
};

export const HumanAuditTab: React.FC<HumanAuditTabProps> = ({ caseId, caseDetails, calls }) => {
  const workflowType = caseDetails?.workflow_type;
  // Agent id for the engine's program-flag resolution — keyed on the case's workflow,
  // mirroring bvagent (see `useEngineAgentId`).
  const agentId = useEngineAgentId(caseDetails?.workflow_id);
  const caseOutputData = caseDetails?.output_data?.data as Record<string, unknown> | undefined;
  const outputSchemaVersion = caseDetails?.output_data?.schema_version ?? '1.0';

  // Editing (output board + disposition + summary) is gated on the auditor being
  // the assignee with output edits allowed. Non-assignees see a read-only view.
  // A transportation or unifiedmedbv human audit also needs a Program Admin or a
  // higher role (BV-2657), so an assignee below that sees it read-only too.
  const isCurrentUserAssigned = useIsCurrentUserAssigned();
  const isProgramAdmin = useIsCaseProgramAdmin(
    caseDetails?.organization_id,
    caseDetails?.program_id,
  );
  const canWorkAudit = canWorkHumanAudit(caseDetails?.state, workflowType, isProgramAdmin);
  const canEdit = Boolean(
    caseDetails?.status_flags?.can_update_output &&
    canWorkAudit &&
    isCurrentUserAssigned(caseDetails?.assigned, caseDetails?.assigned_user_id),
  );

  const { inputGroups, outputGroups } = useSchema(
    caseDetails?.workflow_id,
    caseSchemaVersion(caseDetails),
  );

  // Input editing is gated on the auditor being the assignee with input edits
  // allowed (mirrors the output gate, but keyed on can_update_input).
  const canEditInput = Boolean(
    caseDetails?.status_flags?.can_update_input &&
    canWorkAudit &&
    isCurrentUserAssigned(caseDetails?.assigned, caseDetails?.assigned_user_id),
  );
  const caseInputData = caseDetails?.input_data?.data as Record<string, unknown> | undefined;
  const inputSchemaVersion = caseDetails?.input_data?.schema_version ?? '1.0';

  const updateCaseInput = useUpdateCaseInput();
  // Summary regeneration + post-save (server-side) disposition reconciliation.
  const { isRegenerating, regenerateSummary } = useRegenerateSummary(caseId, caseDetails);
  const { calculateDisposition } = useCalculateDisposition(caseId, caseDetails);

  // --- Output editing (shared with the Summary view via useOutputEditor) ----
  // Explicit Edit / Save / Cancel (no auto-save); the working copy persists only on
  // Save, from which the backend records the per-field corrections. Reconcile the
  // persisted disposition server-side after each save.
  const outputEditor = useOutputEditor({
    caseId,
    validationGroups: outputGroups,
    baselineOutput: caseOutputData,
    schemaVersion: outputSchemaVersion,
    onSaved: () => calculateDisposition(true, workflowType),
  });

  // --- Left pane: toggle between call transcripts and editable input fields ---
  // The auditor can switch the left pane from the transcript viewer to an editable
  // Input board (mirrors the Summary view's Input editing, same phone/fax validation).
  const [leftPaneView, setLeftPaneView] = useState<'transcripts' | 'input'>('transcripts');

  // --- Handoff actions: their own collapsible sidebar in the MIDDLE, with the output
  // board in a collapsible sidebar to its right; without handoff actions the output
  // board is the only sidebar. Handoff actions come from the LATEST audit carrying any
  // (audits are ordered analyzed_at DESC).
  const { audits } = useCaseAudit({ caseRecordId: caseId, feedbackEnabled: false });
  // An empty list is what every branch below already keys off — a case with no handoff
  // actions falls back to the plain layout (output board fills the pane, no handoff
  // container, no escalation composer).
  const handoffActions = useMemo(() => {
    for (const audit of audits) {
      // Case-level actions (field: "case") sort FIRST and render with no field name; an
      // empty-note one carries nothing to show, so an audit left with no action at all
      // falls through to an older one.
      const actions = visibleHandoffActions(audit.handoff_actions ?? []);
      if (actions.length > 0) return actions;
    }
    return [];
  }, [audits]);
  const [isHandoffCollapsed, setIsHandoffCollapsed] = useState(false);
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(false);

  // Editing the field a handoff action points at, inline under its note. Routed
  // through the same output editor as the board so the two can never diverge:
  //  - mid-edit-session → STAGE into the working copy, so the user's pending Save
  //    carries it (a direct write against the baseline would be clobbered by that
  //    Save, silently losing the edit);
  //  - otherwise → persist that one field immediately, board stays read-only.
  const handleHandoffFieldCommit = useCallback(
    async (path: string, value: unknown) => {
      if (outputEditor.isEditing) {
        outputEditor.applyFieldValue(path, value);
        return;
      }
      await outputEditor.commitFieldValue(path, value, 'Field updated');
    },
    [outputEditor],
  );

  // --- "Escalate to human" staging ------------------------------------------
  // Starts EMPTY: the auditor escalates a handoff action from its own row, writing the
  // reason in a note box that opens under that field (BI-1681); Save MOVES the
  // `{field, reason}` pair DOWN into the "Escalate to human caller" section, where it
  // stays visible, editable, and removable — and the action LEAVES the handoff list above
  // (see `pendingHandoffActions`), so each item sits in exactly one of the two places and
  // the list narrows to what is still undecided. Removing a row from the escalation puts
  // its action back in the list, undecided again.
  //
  // Save does NOT hit the API. It holds the rows in the shared store, and the write
  // happens later from the route-to-human-call action (DefaultCaseDetailsPage), which
  // sends them as `action_data.audit_decision.handoff_actions` for the backend to
  // attach to the in-progress human-audit entity. Deferring the write is what keeps
  // the handoff list above intact: an UpdateAudit here would put handoff actions on the
  // (newest) human-audit row, and the list — which reads the newest audit carrying any —
  // would flip from the AI auditor's items to the freshly-escalated ones.
  //
  // Rows therefore SURVIVE Save (they are still pending) and live in the store rather
  // than local state, since the component that finally sends them is elsewhere.
  const stagedEscalations = useStore(state => state.stagedEscalations);
  const setStagedEscalations = useStore(state => state.setStagedEscalations);
  const escalateRows = useMemo(() => stagedEscalations[caseId] ?? [], [stagedEscalations, caseId]);
  const setEscalateRows = useCallback(
    (update: (rows: { field: string; reason: string }[]) => { field: string; reason: string }[]) =>
      setStagedEscalations(caseId, update(stagedEscalations[caseId] ?? [])),
    [caseId, stagedEscalations, setStagedEscalations],
  );
  // A staged row is identified by the action's field — the same string that gets
  // persisted. Case-level actions keep their `case`/blank field, so an escalated one is
  // sent as a case-level handoff (no field name) rather than a made-up field. Indices are
  // normalized to bracket form so the two spellings of one concrete path
  // (`drug.0.coverage.x` from the field renderers, `drug[0].coverage.x` from an authored
  // action) collapse to a single row instead of two.
  const escalationKey = (field: string | undefined) =>
    normalizeHandoffFieldPath((field ?? '').trim());
  const stagedNoteFor = useCallback(
    (field: string | undefined) =>
      escalateRows.find(row => escalationKey(row.field) === escalationKey(field))?.reason,
    [escalateRows],
  );
  // Re-saving an already-escalated action replaces its note in place, so the caller sees
  // one entry per field in the order the auditor escalated them.
  const handleEscalateSave = useCallback(
    (field: string | undefined, note: string) => {
      const key = escalationKey(field);
      setEscalateRows(rows => {
        const index = rows.findIndex(row => escalationKey(row.field) === key);
        if (index >= 0) return rows.map((row, i) => (i === index ? { ...row, reason: note } : row));
        return [...rows, { field: key, reason: note }];
      });
      toast.success('Added to the handoff for the human caller.');
    },
    [setEscalateRows],
  );
  const handleEscalateRemove = useCallback(
    (field: string | undefined) => {
      const key = escalationKey(field);
      setEscalateRows(rows => rows.filter(row => escalationKey(row.field) !== key));
    },
    [setEscalateRows],
  );
  // Notes stay editable in the section below after they land there — edits go straight to
  // the staged row (nothing is written until the case is routed).
  const handleEscalateNoteChange = useCallback(
    (field: string | undefined, note: string) => {
      const key = escalationKey(field);
      setEscalateRows(rows =>
        rows.map(row => (escalationKey(row.field) === key ? { ...row, reason: note } : row)),
      );
    },
    [setEscalateRows],
  );
  // Staged rows carry the RAW field name (that is what gets persisted) but display the
  // same schema breadcrumb as the list above. Resolved once per row — handoffFieldLabel
  // walks every output leaf. A case-level row resolves to '' and is labelled separately.
  const escalateRowLabels = useMemo(
    () => escalateRows.map(row => handoffFieldLabel(outputGroups, row.field)),
    [escalateRows, outputGroups],
  );
  // What the handoff list actually renders: the actions NOT yet escalated. An escalated one
  // is shown only in the "Escalate to human caller" section below, and comes back here the
  // moment it is removed there. Keys are taken from the FULL list so a row keeps its
  // identity — and therefore its open Resolve editor — when an item above it is escalated.
  const pendingHandoffActions = useMemo(
    () =>
      handoffActions
        .map((action, index) => ({ action, key: `${action.field ?? ''}-${index}` }))
        .filter(({ action }) => stagedNoteFor(action.field) === undefined),
    [handoffActions, stagedNoteFor],
  );

  // --- Input editing: explicit Edit / Save / Cancel (NO auto-save) ---------
  // Mirrors the output board below: edits live in a working copy and persist only
  // on Save, with the same phone/fax validation gate.
  const [isEditingInput, setIsEditingInput] = useState(false);
  const [editedInput, setEditedInput] = useState<Record<string, unknown> | undefined>(undefined);
  const [inputFieldErrors, setInputFieldErrors] = useState<Record<string, string>>({});
  const hasInputFieldErrors = Object.keys(inputFieldErrors).length > 0;
  const [inputExpanded, setInputExpanded] = useState<string[]>(() =>
    createAllAccordionIndices(inputGroups.length),
  );
  const workingInput = useMemo<Record<string, unknown>>(
    () => (isEditingInput ? (editedInput ?? caseInputData ?? {}) : (caseInputData ?? {})),
    [isEditingInput, editedInput, caseInputData],
  );
  const allInputExpanded = inputGroups.length > 0 && inputExpanded.length === inputGroups.length;

  useEffect(() => {
    setInputExpanded(createAllAccordionIndices(inputGroups.length));
  }, [inputGroups.length]);

  const scrollToFirstInputError = useCallback(
    (errors: Record<string, string>) => {
      const firstErrorId = Object.keys(errors)[0];
      if (!firstErrorId) return;
      setInputExpanded(createAllAccordionIndices(inputGroups.length));
      setTimeout(() => {
        // Also opens any collapsed nested sub-accordions along the field's path.
        void expandAccordionAncestorsAndScrollToField(firstErrorId);
      }, 500);
    },
    [inputGroups.length],
  );

  const handleInputEditStart = useCallback(() => {
    setEditedInput(JSON.parse(JSON.stringify(caseInputData ?? {})));
    setIsEditingInput(true);
  }, [caseInputData]);

  const handleInputEditCancel = useCallback(() => {
    setIsEditingInput(false);
    setEditedInput(undefined);
    setInputFieldErrors({});
  }, []);

  const handleInputEditSave = useCallback(async () => {
    if (!caseId) return;
    const data = editedInput ?? {};
    // Validate phone/fax input fields before saving; block + highlight + scroll.
    const phoneErrors = collectPhoneFieldErrors(inputGroups, data);
    setInputFieldErrors(phoneErrors);
    if (Object.keys(phoneErrors).length > 0) {
      scrollToFirstInputError(phoneErrors);
      return;
    }
    try {
      await updateCaseInput.mutateAsync({
        caseId,
        inputData: {
          schema_version: inputSchemaVersion,
          data: normalizePhoneFieldsForSave(inputGroups, coerceFormDataToSchema(data, inputGroups)),
        },
      });
      setIsEditingInput(false);
      setEditedInput(undefined);
      setInputFieldErrors({});
      toast.success('Input data updated successfully');
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ||
        (error as { message?: string })?.message ||
        'Failed to save input data';
      toast.error(message);
    }
  }, [
    caseId,
    inputSchemaVersion,
    editedInput,
    inputGroups,
    scrollToFirstInputError,
    updateCaseInput,
  ]);

  const onInputBoardDataChange = useCallback(
    (updated: Record<string, unknown>, fieldPath?: string) => {
      setEditedInput(updated);
      if (!fieldPath) return;
      const error = getPhoneFieldError(inputGroups, fieldPath, getNestedValue(updated, fieldPath));
      setInputFieldErrors(prev => {
        if (!error && !prev[fieldPath]) return prev;
        const next = { ...prev };
        if (error) next[fieldPath] = error;
        else delete next[fieldPath];
        return next;
      });
    },
    [inputGroups],
  );

  // --- Committed-snapshot disposition (shared with Review/Human Call) ------
  // Live, client-side recompute for in-tab feedback; only active while editing.
  const { handleOutputCommit, requiredFields, missingFields, dispositionFields } =
    useCommittedDisposition({
      workflowType,
      baselineOutput: caseOutputData,
      editedOutput: outputEditor.isEditing ? outputEditor.editedOutput : undefined,
      isEditing: outputEditor.isEditing,
      fallbackDisposition: caseDetails?.disposition,
      // Combined engine context: case input (seed) overlaid with committed output.
      inputData: caseInputData,
      // The engine resolves program-flag-gated required fields solely from the case
      // workflow's agent id (BI-1795).
      agentId,
    });

  // --- Calls sorted chronologically, with a per-type index for labels -----
  const orderedCalls = useMemo(() => {
    const withId = calls.filter(c => c.id);
    const sorted = [...withId].sort((a, b) =>
      (a.created_at ?? '').localeCompare(b.created_at ?? ''),
    );
    const counters: Record<string, number> = {};
    return sorted.map(call => {
      const kind = call.type === CallType.CALL_TYPE_HUMAN ? 'Human Call' : 'AI Call';
      counters[kind] = (counters[kind] ?? 0) + 1;
      return { call, label: `${kind} ${counters[kind]}` };
    });
  }, [calls]);

  // --- Global transcript search -------------------------------------------
  // A single query searches every transcript. Each call's header shows a match-count
  // pill (computed by a headless counter without opening it) so the auditor knows
  // which to open; opening one scrolls to its first match with up/down navigation.
  const [transcriptQuery, setTranscriptQuery] = useState('');
  const [openCalls, setOpenCalls] = useState<string[]>([]);
  const [matchCounts, setMatchCounts] = useState<Record<string, number>>({});
  const isSearching = transcriptQuery.trim().length > 0;
  const handleMatchCount = useCallback((callId: string, count: number) => {
    setMatchCounts(prev => (prev[callId] === count ? prev : { ...prev, [callId]: count }));
  }, []);

  // --- Field evidence -> open the source call's transcript on the LEFT --------
  // Output fields carry the AI's value suggestion + reasoning (evidence). Clicking
  // a field's "Review" calls onNavigateToCall(callId, tStart, tEnd); we open that
  // call's accordion item in the left pane and pass the evidence span to its
  // Transcript so the cited lines are highlighted/scrolled to (in-tab, not Review).
  const [evidenceNav, setEvidenceNav] = useState<{
    callId: string;
    range: { start: number; end: number } | null;
  } | null>(null);

  const handleNavigateToCall = useCallback(
    (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => {
      if (!callId) return;
      setOpenCalls(prev => (prev.includes(callId) ? prev : [...prev, callId]));
      const startSeconds = evidenceTimeStart ? parseTimestampToSeconds(evidenceTimeStart) : null;
      const endSeconds = evidenceTimeEnd ? parseTimestampToSeconds(evidenceTimeEnd) : null;
      setEvidenceNav({
        callId,
        range:
          startSeconds !== null && endSeconds !== null
            ? { start: startSeconds, end: endSeconds }
            : null,
      });
    },
    [],
  );

  // Accepting an AI-suggested value opens a confirmation dialog (mirrors the Summary
  // view). On confirm we persist just that field directly — the board stays read-only
  // instead of flipping the whole output into edit mode.
  const [acceptConfirmation, setAcceptConfirmation] = useState<{
    fieldPath: string;
    value: unknown;
    fieldLabel: string;
  } | null>(null);

  const handleAcceptExtracted = useCallback(
    (fieldPath: string, value: unknown, fieldLabel: string) =>
      setAcceptConfirmation({ fieldPath, value, fieldLabel }),
    [],
  );

  const confirmAcceptExtracted = useCallback(async () => {
    if (!acceptConfirmation) return;
    await outputEditor.commitFieldValue(acceptConfirmation.fieldPath, acceptConfirmation.value);
    setAcceptConfirmation(null);
  }, [acceptConfirmation, outputEditor]);

  // Regenerate is offered for summary-editable workflows when not mid-edit/regen.
  const canRegenerate =
    canEdit &&
    isSummaryEditableWorkflow(workflowType) &&
    !outputEditor.isEditing &&
    !isRegenerating;

  // Shared output board (also used by the Summary view). Human-audit specifics —
  // live disposition, AI value suggestions/evidence, regenerate — are passed in.
  // Rendered either as the right pane (no handoff actions) or inside the
  // collapsible Output sidebar (handoff actions occupy the pane). Edit state lives
  // in `outputEditor` here, so collapsing the sidebar never loses in-flight edits.
  // Per-field escalation on the output board (BI-1668 follow-up): every field, at any
  // depth, carries its own Escalate icon + note box, staging into the SAME rows the
  // handoff list's Escalate writes (see `handleEscalateSave`) so both feed one
  // "Escalate to human caller" list and one write on route-to-human-call.
  //
  // Offered to the assignee only while the board is in EDIT mode — the read-only board is
  // for reading, and an affordance on every field of it made the whole board look
  // actionable. Edit mode is also what makes coverage uniform: the registry's
  // drug/procedure output components are display-only, so in edit mode those entities
  // fall through to `NestedFieldAccordion`, whose leaves carry the control with properly
  // indexed paths (`drug.0.coverage.copay`). With no provider the control renders
  // nothing, which is what keeps the Summary / Human Call / AI Audit boards (same
  // renderers) unchanged.
  const canEscalateFields = canEdit && outputEditor.isEditing;

  const outputBoard = (
    <OutputBoardEditor
      editor={outputEditor}
      canEdit={canEdit}
      groups={outputGroups}
      requiredFields={requiredFields}
      missingFields={missingFields}
      isRegenerating={isRegenerating}
      caseId={caseId}
      caseDetails={caseDetails}
      canEditDisposition={canEdit && !isRegenerating}
      editTestId="ha-edit-output-button"
      saveTestId="ha-save-output-button"
      // On the title row beside All / Missed / Required, rather than on a row of its own
      // above them — the sidebar below is headerless for that reason.
      leadingActions={
        <SidebarCollapseButton label="Output" onCollapse={() => setIsOutputCollapsed(true)} />
      }
      // Stays put while the fields scroll beneath it, as the Call Script's board does.
      stickyHeader
      headerActions={
        <>
          {canRegenerate && (
            <SimpleTooltip content="Regenerate Summary" side="bottom">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 w-6 shrink-0 p-0"
                aria-label="Regenerate Summary"
                onClick={() => regenerateSummary(true, workflowType)}
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            </SimpleTooltip>
          )}
          {isRegenerating && (
            <SimpleTooltip content="Regenerating summary…" side="bottom">
              <Loader className="h-3 w-3 animate-spin text-muted-foreground" />
            </SimpleTooltip>
          )}
        </>
      }
      fieldProps={{
        onFieldBlur: handleOutputCommit,
        dispositionFields,
        workflowType,
        caseRecordId: caseId,
        isHumanAudit: true,
        // AI value suggestion + reasoning (evidence) on each output field; the
        // "Review" affordance calls onNavigateToCall to open the cited lines in
        // the left-pane transcript (handleNavigateToCall) — not the Review tab.
        // extractedData supplies the suggested VALUE per field — without it the
        // sparkle/accept affordance is suppressed (editable-field gates on it).
        extractedData: caseDetails?.extracted_data?.output?.data,
        extractedMetadata: caseDetails?.extracted_data?.output?.metadata,
        outputMetadata: caseDetails?.output_data?.metadata,
        onNavigateToCall: handleNavigateToCall,
        // Accepting writes the output, so it follows the same Program Admin rule (BV-2657).
        onAcceptExtracted: canWorkAudit ? handleAcceptExtracted : undefined,
      }}
    />
  );

  const escalatableOutputBoard = canEscalateFields ? (
    <FieldEscalationProvider
      noteFor={stagedNoteFor}
      onSave={handleEscalateSave}
      onRemove={handleEscalateRemove}
    >
      {outputBoard}
    </FieldEscalationProvider>
  ) : (
    outputBoard
  );

  return (
    <div className="flex flex-col divide-y lg:min-h-0 lg:flex-1 lg:flex-row lg:divide-x lg:divide-y-0">
      {/* Left: all call transcripts with a global search highlight. */}
      <section className="flex min-w-0 flex-col lg:flex-[1_1_0%] lg:overflow-hidden">
        <div className="shrink-0 space-y-2 px-4 py-2">
          <div className="flex min-h-7 flex-wrap items-center justify-between gap-2">
            {/* View toggle: call transcripts <-> editable input fields. */}
            <Tabs
              value={leftPaneView}
              onValueChange={v => setLeftPaneView(v as 'transcripts' | 'input')}
            >
              <TabsList className="h-7">
                <TabsTrigger
                  value="transcripts"
                  className="h-6 px-2 text-xs text-muted-foreground data-[state=active]:text-foreground"
                >
                  Call Transcripts
                </TabsTrigger>
                <TabsTrigger
                  value="input"
                  className="h-6 px-2 text-xs text-muted-foreground data-[state=active]:text-foreground"
                >
                  Input
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {/* Input edit controls — mirror the output board's explicit Edit/Save/Cancel. */}
            {leftPaneView === 'input' && (canEditInput || inputGroups.length > 0) && (
              <div className="flex items-center gap-1">
                {isEditingInput && hasInputFieldErrors && (
                  <span className="mr-1 flex items-center gap-1 text-xs font-medium text-destructive">
                    Validation errors found.
                    <button
                      type="button"
                      onClick={() => scrollToFirstInputError(inputFieldErrors)}
                      className="cursor-pointer font-medium text-destructive underline underline-offset-2 hover:no-underline"
                    >
                      View
                    </button>
                  </span>
                )}
                {canEditInput &&
                  (isEditingInput ? (
                    <>
                      <SimpleTooltip content="Save" side="bottom">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleInputEditSave}
                          disabled={updateCaseInput.isPending}
                          className="h-6 w-6 p-0"
                          aria-label="Save"
                          data-testid="ha-save-input-button"
                        >
                          <Save className="h-3 w-3" />
                        </Button>
                      </SimpleTooltip>
                      <SimpleTooltip content="Cancel" side="bottom">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleInputEditCancel}
                          disabled={updateCaseInput.isPending}
                          className="h-6 w-6 p-0"
                          aria-label="Cancel"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </SimpleTooltip>
                    </>
                  ) : (
                    <SimpleTooltip content="Edit" side="bottom">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleInputEditStart}
                        className="h-6 w-6 p-0"
                        aria-label="Edit"
                        data-testid="ha-edit-input-button"
                      >
                        <Edit3 className="h-3 w-3" />
                      </Button>
                    </SimpleTooltip>
                  ))}
                {inputGroups.length > 0 && (
                  <SimpleTooltip
                    content={allInputExpanded ? 'Collapse all' : 'Expand all'}
                    side="bottom"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 w-6 shrink-0 p-0"
                      aria-label={allInputExpanded ? 'Collapse all' : 'Expand all'}
                      onClick={() =>
                        setInputExpanded(toggleAllAccordionItems(inputExpanded, inputGroups.length))
                      }
                    >
                      <ChevronsDownUp className="h-3 w-3" />
                    </Button>
                  </SimpleTooltip>
                )}
              </div>
            )}
          </div>
          {leftPaneView === 'transcripts' && (
            <Input
              value={transcriptQuery}
              onChange={e => setTranscriptQuery(e.target.value)}
              placeholder="Search all transcripts"
              className="pl-2.5"
              aria-label="Search all transcripts"
            />
          )}
        </div>
        <div className="px-4 pb-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          {leftPaneView === 'input' ? (
            <FieldGroupAccordion
              groups={inputGroups}
              data={workingInput}
              section="input"
              isEditable={canEditInput && isEditingInput}
              showCollapseAll={false}
              fieldError={inputFieldErrors}
              onDataChange={onInputBoardDataChange}
              externalExpandedItems={inputExpanded}
              onExpandedItemsChange={setInputExpanded}
              workflowType={workflowType}
              caseRecordId={caseId}
              emptyStateMessage={{
                title: 'No Input Data Available',
                subtitle: 'Request data will appear here',
              }}
            />
          ) : (
            <>
              {/* Headless counters (only while searching): compute each call's match
                  count off the shared cached transcript so headers can show a pill. */}
              {isSearching &&
                orderedCalls.map(({ call }) => (
                  <TranscriptMatchCounter
                    key={`count-${call.id}`}
                    caseId={caseId}
                    call={call}
                    query={transcriptQuery}
                    onCount={handleMatchCount}
                  />
                ))}
              {orderedCalls.length === 0 ? (
                <p className="text-sm text-muted-foreground">No calls on this case yet.</p>
              ) : (
                <Accordion
                  type="multiple"
                  value={openCalls}
                  onValueChange={setOpenCalls}
                  className="space-y-2"
                >
                  {orderedCalls.map(({ call, label }) => {
                    const matchCount = matchCounts[call.id!] ?? 0;
                    return (
                      <AccordionItem
                        key={call.id}
                        value={call.id!}
                        className="rounded-lg border bg-card px-3"
                      >
                        <AccordionTrigger className="py-2 text-sm hover:no-underline">
                          <span className="flex flex-1 items-center gap-2">
                            <span className="font-medium">{label}</span>
                            {call.created_at && (
                              <span className="text-xs text-muted-foreground">
                                <FormattedDateTime value={call.created_at} />
                              </span>
                            )}
                            {isSearching && (
                              <span
                                className={`ml-auto mr-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-md px-1.5 text-xs font-semibold ${
                                  matchCount > 0
                                    ? 'bg-search-highlight text-search-highlight-foreground'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                                aria-label={`${matchCount} match${matchCount === 1 ? '' : 'es'}`}
                              >
                                {matchCount}
                              </span>
                            )}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <CallTranscriptPane
                            caseId={caseId}
                            call={call}
                            searchQuery={transcriptQuery}
                            evidenceHighlightRange={
                              evidenceNav && evidenceNav.callId === call.id
                                ? evidenceNav.range
                                : null
                            }
                          />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </>
          )}
        </div>
      </section>

      {/* Middle/right: BOTH work surfaces are collapsible sidebars — the
          transcripts/input pane takes the remaining width. Handoff actions sit in the
          MIDDLE (next to the transcripts they are read against) and the output board is
          OUTERMOST on the right (BI-1681). Collapsing unmounts the children, which is
          safe for both: output edit state is hoisted into outputEditor, the escalated
          notes live in the shared store (stagedEscalations), and each row's Resolve
          editor holds only an uncommitted draft — committed values are already
          persisted. */}
      {/* The sidebar also opens for a case with NO AI handoff actions once the auditor
          escalates an output field from the board — otherwise their escalations would be
          staged with nowhere to read, edit, or remove them. */}
      {(handoffActions.length > 0 || escalateRows.length > 0) && (
        <CollapsibleSidebar
          label="Handoff actions"
          isCollapsed={isHandoffCollapsed}
          onCollapsedChange={setIsHandoffCollapsed}
          expandedWidth="w-[480px]"
          hideHeader
        >
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-3 pt-1">
            {/* The handoff action items still undecided, each offering Escalate (write a
                note for the caller, right under the field) or Resolve (edit the flagged
                output field inline). Escalating one moves it into the section below and
                out of this list; removing it there brings it back. Case-level actions come
                first and render with no field name, so one can be edited into a free-form
                custom handoff. */}
            {handoffActions.length > 0 && (
              <div className="bg-card border rounded-lg px-3 py-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">Handoff actions</h3>
                  {escalateRows.length > 0 && (
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {escalateRows.length} escalated
                    </span>
                  )}
                </div>
                {pendingHandoffActions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    All handoff actions have been escalated. Remove one below to bring it back.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {pendingHandoffActions.map(({ action, key }) => (
                      <HandoffActionRow
                        key={key}
                        action={action}
                        onEscalate={note => handleEscalateSave(action.field, note)}
                        canEdit={canEdit}
                        outputGroups={outputGroups}
                        outputData={outputEditor.workingOutput}
                        onCommitField={handleHandoffFieldCommit}
                        caseRecordId={caseId}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* "Escalate to human caller": where a row's Save lands. Each entry is the
                escalated field plus the note as edited in that row, still editable here
                and removable. Nothing is written yet — the rows are attached to the
                in-progress human audit when the case is routed to a human call
                (DefaultCaseDetailsPage). */}
            {canEdit && (
              <div className="bg-card border rounded-lg px-3 py-3">
                <h3 className="mb-2 text-sm font-semibold text-foreground">
                  Escalate to human caller
                </h3>
                {escalateRows.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Click Escalate on a handoff action above — or, while editing the Output board,
                    on any field there — write the note, and Save to add it here.
                  </p>
                ) : (
                  <>
                    <p className="mb-2 text-[11px] text-muted-foreground">
                      Attached to the case when you route it to a human call.
                    </p>
                    <ul className="space-y-3">
                      {escalateRows.map((row, index) => (
                        <li key={`${row.field}-${index}`} className="flex flex-col gap-1">
                          <div className="flex items-center justify-between gap-2">
                            {/* Schema breadcrumb, matching the handoff list above — the row
                                still carries the raw `field` for the write. A case-level
                                escalation has no field name, so it is labelled as such. */}
                            <span className="min-w-0 break-words text-xs font-medium text-foreground">
                              {escalateRowLabels[index] || 'Case-level note'}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                              aria-label={`Remove ${escalateRowLabels[index] || 'the case-level note'} from the escalation`}
                              onClick={() => handleEscalateRemove(row.field)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <Textarea
                            value={row.reason}
                            onChange={e => handleEscalateNoteChange(row.field, e.target.value)}
                            placeholder="Reason for escalation..."
                            className="min-h-[56px] text-sm"
                            aria-label={`Escalation note for ${escalateRowLabels[index] || 'the case'}`}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </CollapsibleSidebar>
      )}

      <CollapsibleSidebar
        label="Output"
        isCollapsed={isOutputCollapsed}
        onCollapsedChange={setIsOutputCollapsed}
        expandedWidth="w-[624px]"
        // The board's own title row carries the collapse control (`leadingActions` above).
        headerless
      >
        {/* No top padding: the board's sticky title row carries it (see stickyHeader). */}
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-3">
          {escalatableOutputBoard}
        </div>
      </CollapsibleSidebar>

      {/* Confirmation dialog for accepting an AI-extracted value (mirrors Summary view). */}
      <Dialog open={!!acceptConfirmation} onOpenChange={() => setAcceptConfirmation(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Extracted Value?</DialogTitle>
            <DialogDescription>
              This will update &quot;{acceptConfirmation?.fieldLabel}&quot; to &quot;
              {typeof acceptConfirmation?.value === 'object'
                ? JSON.stringify(acceptConfirmation?.value)
                : String(acceptConfirmation?.value ?? '')}
              &quot;. You can edit this value later, but there&apos;s no one-click undo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptConfirmation(null)}>
              Cancel
            </Button>
            <Button onClick={confirmAcceptExtracted} disabled={outputEditor.isSaving}>
              Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
